import React, { useState, useEffect } from "react";
import axios from "axios";
import Structure from "../components/StructureView";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";

const API_URL = `${API_BASE_URL}/api`;

const AnnouncementService = {
  getAnnouncements: async (companyId, token, userType) => {
    try {
      const response = await axios.get(`${API_URL}/announcements`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching announcements:", error);
      throw error;
    }
  },
  getAnnouncementsByDepartment: async (departmentId, companyId, token, userType) => {
    try {
      const response = await axios.get(
        `${API_URL}/announcements/department/${departmentId}`,
        {
          headers: {
            'user-id': token,
            'user-type': userType,
            'company-id': companyId
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching department announcements:", error);
      throw error;
    }
  }
};

const formatDate = (dateString) => {
  // Handle null, undefined, or empty string
  if (!dateString) {
    return "Invalid Date";
  }
  
  const date = new Date(dateString);
  
  // Check if the date is invalid
  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }
  
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric"
  }).format(date);
};

const ApiService = {
  getEvents: async (companyId, token, userType) => {
    try {
      const response = await axios.get(`${API_URL}/events`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching events:", error);
      throw error;
    }
  },
  
  getLeaveBalances: async (empNo, companyId, token, userType) => {
    try {
      const response = await axios.get(`${API_URL}/leaveBalances/${empNo}`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      // If leave balance is not found yet, initialize it and return the created balance
      if (error.response && error.response.status === 404) {
        try {
          const initResponse = await axios.post(
            `${API_URL}/leaveBalances`,
            { empNo },
            {
              headers: {
                'user-id': token,
                'user-type': userType,
                'company-id': companyId
              }
            }
          );
          return initResponse.data;
        } catch (initErr) {
          console.error("Error initializing leave balances:", initErr);
          throw initErr;
        }
      }
      console.error("Error fetching leave balances:", error);
      throw error;
    }
  },
  
  getAllEmployees: async (companyId, token, userType) => {
    try {
      const response = await axios.get(`${API_URL}/employees`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching employees:", error);
      throw error;
    }
  },
  
  getTodayAttendance: async (companyId, token, userType) => {
    try {
      const response = await axios.get(`${API_URL}/attendance/today`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      
      return {
        present: response.data.stats.present || 0,
        absent: response.data.stats.absent || 0,
        onLeave: response.data.stats.onLeave || 0,
        count: response.data.stats.total || 0
      };
    } catch (error) {
      console.error("Error fetching today's attendance:", error);
      
      try {
        const today = new Date().toISOString().split('T')[0];
        const statsResponse = await axios.get(`${API_URL}/attendance/stats`, {
          headers: {
            'user-id': token,
            'user-type': userType,
            'company-id': companyId
          }
        });
        const dailyStats = statsResponse.data.dailyStats || [];
        const todayStats = dailyStats.find(
          stat => new Date(stat.date).toISOString().split('T')[0] === today
        ) || { count: 0, present: 0, absent: 0, onLeave: 0 };
        
        return {
          present: todayStats.present || 0,
          absent: todayStats.absent || 0,
          onLeave: todayStats.onLeave || 0,
          count: todayStats.count || 0
        };
      } catch (fallbackError) {
        console.error("Fallback method also failed:", fallbackError);
        throw error;
      }
    }
  },

  getOnLeaveCount: async (companyId, token, userType) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API_URL}/leaveRequests`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      
      const onLeave = response.data.filter(leave => 
        leave.status === 'Approved' && 
        new Date(leave.startDate) <= new Date(today) && 
        new Date(leave.endDate) >= new Date(today)
      );
      return onLeave.length;
    } catch (error) {
      console.error("Error fetching on-leave count:", error);
      throw error;
    }
  }
};

const Dashboard = () => {
  const { user, userType, companyId } = useAuth();
  const isAdmin = userType === "user";
  
  const [events, setEvents] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [announcementMonth, setAnnouncementMonth] = useState(new Date().getMonth());
  const [announcementYear, setAnnouncementYear] = useState(new Date().getFullYear());
  const [leaveBalances, setLeaveBalances] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [error, setError] = useState(null);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [todayAttendance, setTodayAttendance] = useState({ present: 0, absent: 0, onLeave: 0, total: 0 });
  const [onLeaveCount, setOnLeaveCount] = useState(0);
  const [announcements, setAnnouncements] = useState([]);
  const [departmentId, setDepartmentId] = useState(null);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !companyId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        fetchEvents(token);
        fetchAnnouncements(token);
        
        if (user.departmentId) {
          setDepartmentId(user.departmentId);
        }
        
        if (isAdmin) {
          try {
            const employees = await ApiService.getAllEmployees(companyId, token, userType);
            setTotalEmployees(employees.length);
            
            const todayStats = await ApiService.getTodayAttendance(companyId, token, userType);
            setTodayAttendance({
              present: todayStats.present || 0,
              absent: todayStats.absent || 0,
              onLeave: todayStats.onLeave || 0,
              total: todayStats.count || employees.length
            });
            
            const leaveCount = await ApiService.getOnLeaveCount(companyId, token, userType);
            setOnLeaveCount(leaveCount);
          } catch (statsError) {
            console.error("Error fetching admin statistics:", statsError);
            setError("Failed to load statistical data");
          }
        } else if (user.empNo) {
          fetchLeaveBalances(user.empNo, token);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setError("Failed to load dashboard data");
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user, isAdmin, companyId, userType]);

  useEffect(() => {
    if (departmentId && companyId) {
      const token = localStorage.getItem('token');
      fetchAnnouncements(token);
    }
  }, [departmentId, companyId]);

  const fetchLeaveBalances = async (empNo, token) => {
    try {
      const balanceData = await ApiService.getLeaveBalances(empNo, companyId, token, userType);
      
      if (balanceData) {
        setLeaveBalances({
          id: balanceData.id,
          balances: {
            sickLeave: balanceData.sickLeave,
            casualLeave: balanceData.casualLeave,
            earnedLeave: balanceData.earnedLeave
          }
        });
      }
    } catch (error) {
      console.error("Error fetching leave balances:", error);
      setError("Failed to load leave balance data");
    }
  };

  const fetchEvents = async (token) => {
    try {
      const eventsList = await ApiService.getEvents(companyId, token, userType);
      setEvents(eventsList || []);
    } catch (error) {
      console.error("Error fetching events:", error);
      setError("Failed to load events data");
    }
  };

  const fetchAnnouncements = async (token) => {
    try {
      setAnnouncementsLoading(true);
      let announcementsList;
      
      if (departmentId && !isAdmin) {
        announcementsList = await AnnouncementService.getAnnouncementsByDepartment(
          departmentId, companyId, token, userType
        );
      } else {
        announcementsList = await AnnouncementService.getAnnouncements(
          companyId, token, userType
        );
      }
      
      setAnnouncements(announcementsList || []);
      setAnnouncementsLoading(false);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      setError("Failed to load announcements data");
      setAnnouncementsLoading(false);
    }
  };

  const getFilteredEvents = () => {
    return events.filter((event) => {
      // Validate event date exists
      if (!event.eventDate) return false;
      
      const eventDate = new Date(event.eventDate);
      
      // Check if date is valid
      if (isNaN(eventDate.getTime())) return false;
      
      return (
        eventDate.getMonth() === selectedMonth &&
        eventDate.getFullYear() === selectedYear
      );
    });
  };

  const getFilteredAnnouncements = () => {
    return announcements.filter((announcement) => {
      // Validate announcement date exists
      if (!announcement.created_at) return false;
      
      const announcementDate = new Date(announcement.created_at);
      
      // Check if date is valid
      if (isNaN(announcementDate.getTime())) return false;
      
      return (
        announcementDate.getMonth() === announcementMonth &&
        announcementDate.getFullYear() === announcementYear
      );
    });
  };
  
  const handleMonthChange = (direction) => {
    const newDate = new Date(selectedYear, selectedMonth + direction, 1);
    setSelectedMonth(newDate.getMonth());
    setSelectedYear(newDate.getFullYear());
  };
  
  const handleAnnouncementMonthChange = (direction) => {
    const newDate = new Date(announcementYear, announcementMonth + direction, 1);
    setAnnouncementMonth(newDate.getMonth());
    setAnnouncementYear(newDate.getFullYear());
  };

  const filteredEvents = getFilteredEvents();
  const filteredAnnouncements = getFilteredAnnouncements();

  const renderTabContent = () => {
    switch (activeTab) {
      case "structure":
        return <Structure viewOnly={true} />;
      case "dashboard":
      default:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {isAdmin && (
              <div className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all hover:shadow-xl md:col-span-2">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4">
                  <h2 className="text-xl font-bold text-white">Company Overview</h2>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-indigo-50 p-4 rounded-lg text-center">
                      <span className="block text-sm text-gray-600">Total Employees</span>
                      <span className="font-bold text-3xl text-indigo-700">{totalEmployees}</span>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <span className="block text-sm text-gray-600">Present Today</span>
                      <span className="font-bold text-3xl text-green-700">{todayAttendance.present}</span>
                      <span className="block text-xs text-gray-500">
                        {totalEmployees > 0 ? 
                          `${Math.round((todayAttendance.present / totalEmployees) * 100)}%` : 
                          '0%'}
                      </span>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-lg text-center">
                      <span className="block text-sm text-gray-600">On Leave Today</span>
                      <span className="font-bold text-3xl text-amber-700">{onLeaveCount}</span>
                      <span className="block text-xs text-gray-500">
                        {totalEmployees > 0 ? 
                          `${Math.round((onLeaveCount / totalEmployees) * 100)}%` : 
                          '0%'}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div className="flex h-2.5 rounded-full overflow-hidden">
                        <div 
                          className="bg-green-500" 
                          style={{ 
                            width: `${totalEmployees > 0 ? (todayAttendance.present / totalEmployees) * 100 : 0}%` 
                          }}
                        ></div>
                        <div 
                          className="bg-amber-500" 
                          style={{ 
                            width: `${totalEmployees > 0 ? (onLeaveCount / totalEmployees) * 100 : 0}%` 
                          }}
                        ></div>
                        <div 
                          className="bg-red-500" 
                          style={{ 
                            width: `${totalEmployees > 0 ? 
                              ((totalEmployees - todayAttendance.present - onLeaveCount) / totalEmployees) * 100 : 0}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Present: {todayAttendance.present}</span>
                      <span>On Leave: {onLeaveCount}</span>
                      <span>Absent: {totalEmployees - todayAttendance.present - onLeaveCount}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {!isAdmin && leaveBalances && (
              <div className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all hover:shadow-xl">
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4">
                  <h2 className="text-xl font-bold text-white">Leave Balances Per Years</h2>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-3 rounded-lg text-center">
                      <span className="block text-sm text-gray-600">Sick Leave</span>
                      <span className="font-bold text-2xl text-blue-700">{leaveBalances.balances.sickLeave}</span>
                      <span className="block text-xs text-gray-500">days</span>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg text-center">
                      <span className="block text-sm text-gray-600">Casual</span>
                      <span className="font-bold text-2xl text-green-700">{leaveBalances.balances.casualLeave}</span>
                      <span className="block text-xs text-gray-500">days</span>
                    </div>
                    <div className="bg-purple-50 p-3 rounded-lg text-center">
                      <span className="block text-sm text-gray-600">Earned</span>
                      <span className="font-bold text-2xl text-purple-700">{leaveBalances.balances.earnedLeave}</span>
                      <span className="block text-xs text-gray-500">days</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all hover:shadow-xl">
              <div className="bg-gradient-to-r from-red-500 to-red-600 p-4">
                <h2 className="text-xl font-bold text-white">Upcoming Events</h2>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <button
                    className="bg-red-100 text-red-600 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors"
                    onClick={() => handleMonthChange(-1)}
                    aria-label="Previous Month"
                  >
                    ← Prev
                  </button>
                  <div className="text-lg font-bold text-gray-800">
                    {new Date(selectedYear, selectedMonth).toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    })}
                  </div>
                  <button
                    className="bg-red-100 text-red-600 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors"
                    onClick={() => handleMonthChange(1)}
                    aria-label="Next Month"
                  >
                    Next →
                  </button>
                </div>
                
                {loading ? (
                  <div className="text-center py-4 text-gray-600">
                    Loading...
                  </div>
                ) : error ? (
                  <div className="text-center py-4 text-red-600">
                    {error}
                  </div>
                ) : filteredEvents.length > 0 ? (
                  <div className="space-y-4">
                    {filteredEvents.slice(0, 3).map((event) => (
                      <div
                        key={event.id}
                        className={`p-3 rounded-lg ${
                          event.isHoliday
                            ? "bg-red-50 border-l-4 border-red-500"
                            : "bg-green-50 border-l-4 border-green-500"
                        }`}
                      >
                        <div className="font-bold text-gray-800">{event.eventName}</div>
                        <div className="text-sm text-gray-600">{formatDate(event.eventDate)}</div>
                        <div className="mt-1 text-sm text-gray-700">{event.description}</div>
                        <div className="mt-1 text-xs font-semibold">
                          <span className={`px-2 py-1 rounded-full ${event.isHoliday ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>
                            {event.isHoliday ? "Holiday" : "Working Day"}
                          </span>
                        </div>
                      </div>
                    ))}
                    {filteredEvents.length > 3 && (
                      <div className="text-center">
                        <button className="text-blue-600 hover:underline text-sm">
                          View All ({filteredEvents.length})
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-600">
                    No events for this month.
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all hover:shadow-xl">
              <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 p-4">
                <h2 className="text-xl font-bold text-white">Announcements</h2>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <button
                    className="bg-yellow-100 text-yellow-600 px-4 py-2 rounded-lg hover:bg-yellow-200 transition-colors"
                    onClick={() => handleAnnouncementMonthChange(-1)}
                    aria-label="Previous Month"
                  >
                    ← Prev
                  </button>
                  <div className="text-lg font-bold text-gray-800">
                    {new Date(announcementYear, announcementMonth).toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    })}
                  </div>
                  <button
                    className="bg-yellow-100 text-yellow-600 px-4 py-2 rounded-lg hover:bg-yellow-200 transition-colors"
                    onClick={() => handleAnnouncementMonthChange(1)}
                    aria-label="Next Month"
                  >
                    Next →
                  </button>
                </div>
                
                {announcementsLoading ? (
                  <div className="text-center py-4 text-gray-600">
                    Loading...
                  </div>
                ) : error ? (
                  <div className="text-center py-4 text-red-600">
                    {error}
                  </div>
                ) : filteredAnnouncements.length > 0 ? (
                  <div className="space-y-4">
                    {filteredAnnouncements.slice(0, 3).map((announcement) => (
                      <div
                        key={announcement.id}
                        className="p-3 rounded-lg bg-yellow-50 border-l-4 border-yellow-500"
                      >
                        <div className="font-bold text-gray-800">{announcement.title}</div>
                        <div className="text-sm text-gray-600">{formatDate(announcement.created_at)}</div>
                        <div className="mt-1 text-sm text-gray-700 line-clamp-2">{announcement.content}</div>
                        <div className="mt-1 text-xs font-semibold">
                          <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">
                            {announcement.department_name || "General"}
                          </span>
                        </div>
                      </div>
                    ))}
                    {filteredAnnouncements.length > 3 && (
                      <div className="text-center">
                        <button className="text-blue-600 hover:underline text-sm">
                          View All ({filteredAnnouncements.length})
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-600">
                    No announcements for this month.
                  </div>
                )}
              </div>
            </div>
          </div>
        );
    }
  };

  if (!user) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-r from-blue-800 to-indigo-900 min-h-screen flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Please Log In</h2>
          <p className="text-gray-600 mb-6">You need to be logged in to view the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-r from-blue-800 to-indigo-900 min-h-screen">
      {isAdmin ? (
        <div className="bg-white p-4 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto">
          <h3 className="text-lg font-semibold text-gray-700">Admin Dashboard</h3>
          <p className="text-gray-600">Welcome, {user?.name || user?.email}. You have administrative access.</p>
        </div>
      ) : (
        <div className="bg-white p-4 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto">
          <h3 className="text-lg font-semibold text-gray-700">Employee Dashboard</h3>
          <p className="text-gray-600">Welcome, {user?.name || user?.email}. View your personal dashboard below.</p>
        </div>
      )}

      <div className="flex justify-center mb-6">
        <div className="bg-white bg-opacity-20 p-1 rounded-lg inline-flex">
          <button
            onClick={() => setActiveTab("dashboard")}
            className={`py-2 px-4 text-sm font-medium rounded-md transition-colors duration-200 ${
              activeTab === "dashboard"
                ? "bg-white text-blue-700"
                : "text-white hover:bg-white hover:bg-opacity-10"
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab("structure")}
            className={`py-2 px-4 text-sm font-medium rounded-md transition-colors duration-200 ${
              activeTab === "structure"
                ? "bg-white text-blue-700"
                : "text-white hover:bg-white hover:bg-opacity-10"
            }`}
          >
            Company Structure
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-6 text-white text-xl">Loading dashboard data...</div>
      ) : (
        <>
          {renderTabContent()}
        </>
      )}
    </div>
  );
};

export default Dashboard;